import React, { useCallback, useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { Grid, Typography, FormControlLabel, Checkbox } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import moment from 'moment';
import RadioButton from 'components/form/radio/radio-button';
import RadioGroup from 'components/form/radio/radio-group';
import SubHeader from 'components/form/header/subheader';
import DatetimePicker from 'components/form/datepicker/datetime-picker';
import { MediumSpacer, SmallSpacer } from 'components/spacer/spacer';
import { ReactSelect } from 'components/form/field/react-select';
import { setSidebarFilter } from 'actions/action-sidebar-filters';
import {
  DATE_OPTIONS,
  PA,
  RS,
  FA,
  CSL,
  OR,
  QRE,
  FDC,
  AR,
  DUR,
  FC,
  CATEGORY_ARCHIVED,
  CATEGORY_DONE,
  THERAPIES,
  SMS_RELATED_TASK,
  STAR,
  LAST_NAMES_BY_LETTER,
  SCHEDULE_OUTREACH_STATUS_COMMUNICATION_PHONE_ID,
  SCHEDULE_OUTREACH_STATUS_COMMUNICATION_INPERSON_ID,
  FILL_COORDINATION_STATUS_SECURE_LINK,
  DC,
  INT,
  DATA_TASK_COUNTS_FETCH,
} from 'constants/index';
import {
  methodOfDelivery,
  financialTypeFilterValues,
  getFinancialTypeFilterValueByKey,
  internalFoundationFilterValues,
  fcInProgressReasons,
  taskStatusesExceptionToShowInFilters,
  displayOnlyFdcValues,
} from 'constants/lists';

import { getAuditCategory, auditCategoriesRaw } from 'services/utils/audit-service';
import {
  getDatesFromSidebarFilters,
  getRadioCheckedDateFilter,
  evaluateDate,
  outreachTypeByNameForFilters,
  calcIncidentCombinedResponsibles,
} from 'services/utils/filters-service';
import { styles } from './task-filter-panel-styles';
import FillCycleFilter from './filter-components/fill-coordination/fill-cycle-filter';
import DispensingPharmacyFilter from './filter-components/fill-coordination/dispensing-pharmacy-filter';

const arListValues = [
  {
    label: 'Automatic',
    value: 'automatic',
  },
  {
    label: 'Manual',
    value: 'manual',
  },
];

const arCommunicationTypeValues = [
  {
    label: 'Phone',
    value: SCHEDULE_OUTREACH_STATUS_COMMUNICATION_PHONE_ID,
  },
  {
    label: 'In person',
    value: SCHEDULE_OUTREACH_STATUS_COMMUNICATION_INPERSON_ID,
  },
];

const queueAllowsAuditFilters = [];

const TaskFilterPanel = props => {
  const {
    classes,
    lookups,
    sidebarFilters,
    sidebarTaskType,
    taskStatuses,
    setSidebarFilter, // eslint-disable-line
  } = props;
  const dispatch = useDispatch();
  // #region component variables

  const methodOfDeliveryForCustomer = methodOfDelivery;

  const serviceGroups = sidebarFilters.serviceGroups || [];
  const therapyTypes = sidebarFilters.therapyTypes || [];
  const serviceGroupsIds = serviceGroups.map(serviceGroup => serviceGroup.idValue);
  const taskClinics = sidebarFilters.clinics || [];

  const [localFilters, setLocalFilters] = useState(() => ({
    ...sidebarFilters,
    therapyTypes: sidebarFilters.therapyTypes || [],
    serviceGroups: sidebarFilters.serviceGroups || [],
    clinics: sidebarFilters.clinics || [],
  }));

  const radioCheckedDate = getRadioCheckedDateFilter(localFilters);
  const qreResponsibles = lookups ? calcIncidentCombinedResponsibles(lookups) : [];

  useEffect(() => {
    if (!setSidebarFilter) {
      return;
    }

    if (!sidebarFilters.from && !sidebarFilters.to && !sidebarFilters.date) {
      setSidebarFilter('task', {
        date: DATE_OPTIONS.TODAY,
        from: null,
        to: null,
      });
    }
  }, [sidebarFilters.from, sidebarFilters.to, sidebarFilters.date, setSidebarFilter]);

  const dateChipsEnabled = !sidebarFilters.completed;
  const clinicsForServiceGroups =
    localFilters.serviceGroups.length > 0
      ? lookups.customerClinics.filter(clinic =>
          localFilters.serviceGroups.some(
            serviceGroup => serviceGroup.idValue === clinic.service_group_id,
          ),
        )
      : lookups.customerClinics;

  const outreachTypeByName = outreachTypeByNameForFilters(taskStatuses.outreach_types);

  const onlyActiveUserQRE =
    localFilters &&
    localFilters.incidentResponsibleParty &&
    localFilters.incidentResponsibleParty.length === 1 &&
    localFilters.incidentResponsibleParty[0] === `u${window.USER.id}`;

  const buildOutreachReasons = () => {
    return lookups.lookupScheduleOutreachReasons.map(reason => ({
      value: reason.id,
      label: reason.name,
    }));
  };

  const arReasonFilterValues =
    buildOutreachReasons().filter(reason => sidebarFilters.arReasonIds?.includes(reason.value)) ||
    [];

  useEffect(() => {
    setLocalFilters({
      ...sidebarFilters,
      therapyTypes: sidebarFilters.therapyTypes || [],
      serviceGroups: sidebarFilters.serviceGroups || [],
      clinics: sidebarFilters.clinics || [],
    });
  }, [sidebarFilters]);

  const handleApplyFilters = () => {
    setSidebarFilter('task', { ...localFilters });
    dispatch({
      type: DATA_TASK_COUNTS_FETCH,
      payload: { forceTaskType: sidebarTaskType },
    });
  };
  const updateLocalFilter = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // #endregion component variables

  // #region  component state

  const [statusOptions, setStatusOptions] = useState([]);
  const [statusIdsLookup, setStatusIdsLookup] = useState({});
  const [taskStatusValues, setTaskStatusValues] = useState([]);
  const [enrollmentStatusValues, setEnrollmentStatusValues] = useState([]);
  const [administrationStatusValues, setAdministrationStatusValues] = useState([]);
  const [arReasonValues, setARReasonValues] = useState(arReasonFilterValues);
  const [isSelectAllSecureLinkFC, setIsSelectAllSecureLinkFC] = useState(false);
  // #endregion component state

  // #region component functions

  const initialTypesForOR = () => {
    const retArr = [];
    const allTypeVals =
      sidebarFilters.orType && sidebarFilters.orType.length > 0
        ? sidebarFilters.orType.map(type =>
            outreachTypeByName.find(x =>
              x.related
                .split(',')
                .map(strVal => Number(strVal))
                .some(typeObj => typeObj === type),
            ),
          )
        : [];
    allTypeVals.forEach(allType => {
      if (!retArr.some(retType => retType.label === allType.name) || retArr.length === 0) {
        retArr.push({ label: allType.name, value: allType.related });
      }
    });
    return retArr;
  };

  const initialTypesForAuditRule = () => {
    const allTypeVals =
      sidebarFilters.auditWarningsRule && sidebarFilters.auditWarningsRule.length > 0
        ? sidebarFilters.auditWarningsRule.map(auditRule => ({
            value: auditRule,
            label:
              lookups.auditConstants &&
              lookups.auditConstants.find(rule => rule.arbor_audit_definition_id === auditRule)
                ? lookups.auditConstants.find(rule => rule.arbor_audit_definition_id === auditRule)
                    .audit_item
                : null,
          }))
        : [];
    return allTypeVals;
  };

  const buildTaskStatusValues = () => {
    const uniqStatusNamesArr = taskStatuses?.statuses[sidebarTaskType?.toLowerCase()] ?? [];
    const uniqStatusNames = uniqStatusNamesArr.reduce((accum, status) => {
      if (sidebarFilters.statusIds?.[sidebarTaskType]?.includes(status.id)) {
        accum.add(status.status);
      }
      return accum;
    }, new Set());

    return [...uniqStatusNames].map(statusName => ({
      label: statusName,
      value: statusName,
    }));
  };

  const buildTaskStatusLookupOptions = () => {
    return (taskStatuses?.statuses[sidebarTaskType?.toLowerCase()] || []).reduce(
      (accum, statusWithCategory) => {
        if (
          taskStatusesExceptionToShowInFilters.includes(statusWithCategory.id) ||
          ![CATEGORY_DONE, CATEGORY_ARCHIVED].includes(statusWithCategory.category_id)
        ) {
          if (!accum.statusIdsOptionLookup[statusWithCategory.status]) {
            accum.statusSelectorOptions.push({
              label: statusWithCategory.status,
              value: statusWithCategory.status,
            });
            accum.statusIdsOptionLookup[statusWithCategory.status] = [statusWithCategory.id];
          } else {
            accum.statusIdsOptionLookup[statusWithCategory.status].push(statusWithCategory.id);
          }
        }
        return accum;
      },
      {
        statusSelectorOptions: [],
        statusIdsOptionLookup: {},
      },
    );
  };

  const buildEnrollmentStatusOptions = () => {
    const statuses = new Set();
    return lookups.enrollmentStatuses.reduce((accum, enrollmentStatus) => {
      if (!statuses.has(enrollmentStatus.status)) {
        statuses.add(enrollmentStatus.status);
        accum.push({
          label: enrollmentStatus.status,
          value: enrollmentStatus.id,
        });
      }
      return accum;
    }, []);
  };

  const buildAdministrationStatusOptions = () => {
    const statuses = new Set();
    const initialAdminStatuses = lookups.lookupStatusReasons.filter(
      s => s.resource === 'therapy_administration',
    );
    return initialAdminStatuses.reduce((accum, administrationStatus) => {
      if (!statuses.has(administrationStatus.status)) {
        statuses.add(administrationStatus.status);
        accum.push({
          label: administrationStatus.status,
          value: administrationStatus.status,
        });
      }
      return accum;
    }, []);
  };

  const buildInitialEnrollmentStatusValues = selectedStatusIds =>
    selectedStatusIds.map(statusId => {
      const lookupStatus = lookups.enrollmentStatuses.find(status => status.id === statusId);
      return {
        value: lookupStatus.id,
        label: lookupStatus.status,
      };
    });

  const buildInitialAdministrationStatusValues = selectedStatusIds =>
    selectedStatusIds.map(statusName => {
      const lookupStatus = lookups.lookupStatusReasons
        .filter(s => s.resource === 'therapy_administration')
        .find(statusVar => statusVar?.status === statusName);
      return {
        value: lookupStatus?.status,
        label: lookupStatus?.status,
      };
    });

  const handleToDateChange = e => {
    const newDate = e instanceof moment ? moment(e).format('YYYY-MM-DD') : evaluateDate(e);
    updateLocalFilter('to', newDate === 'noDate' ? null : newDate);
  };

  const handleHideInternalFundingFA = e => {
    const value = e.target.checked;
    updateLocalFilter('hideInternalFundingFA', value);
  };

  const handleOnlyUserTasksQre = e => {
    if (!onlyActiveUserQRE) {
      updateLocalFilter('incidentResponsibleParty', [`u${window.USER.id}`]);
    } else {
      updateLocalFilter('incidentResponsibleParty', []);
    }
  };

  const handleFromDateChange = e => {
    const newDate = e instanceof moment ? moment(e).format('YYYY-MM-DD') : evaluateDate(e);
    updateLocalFilter('from', newDate === 'noDate' ? null : newDate);
  };

  const handleExcludeOverdueARsChange = e => {
    const value = e.target.checked;
    updateLocalFilter('excludeOverdueARs', value);
  };

  const handleColdChain = e => {
    const value = e.target.checked;
    updateLocalFilter('coldChain', value);
  };

  const handleUrgent = e => {
    const value = e.target.checked;
    updateLocalFilter('urgent', value);
  };

  const handleAdherencePackaging = e => {
    const value = e.target.checked;

    updateLocalFilter('adherencePackaging', value);
  };

  const handleExcludeAutoCompletedFdc = e => {
    const value = e.target.checked;
    updateLocalFilter('excludeAutoCompletedFdc', value);
  };

  const handleMedicationAvailabiltiyINT = e => {
    const value = e.target.checked;
    updateLocalFilter('medicationAvailabiltiyINT', value);
  };

  const handleDisplayOnlyFC = value => {
    const selectedDisplayOnly = (value || []).map(it => it.value);

    updateLocalFilter('displayOnlyFC', selectedDisplayOnly);
  };

  const handleDisplayOnlyDC = value => {
    const selectedDisplayOnly = (value || []).map(it => it.value);

    updateLocalFilter('displayOnlyDC', selectedDisplayOnly);
  };

  const handleDisplayOnlyCSL = value => {
    const selectedDisplayOnly = (value || []).map(it => it.value);

    updateLocalFilter('displayOnlyCSL', selectedDisplayOnly);
  };

  const handleDisplayOnlyINT = value => {
    const selectedDisplayOnly = (value || []).map(it => it.value);

    updateLocalFilter('displayOnlyINT', selectedDisplayOnly);
  };

  const handleDisplayOnlyRS = value => {
    const selectedDisplayOnly = (value || []).map(it => it.value);

    updateLocalFilter('displayOnlyRS', selectedDisplayOnly);
  };

  const handleServiceGroupChange = value => {
    const selectedServiceGroups = value
      ? value.map(it => ({
          displayValue: it.label,
          idValue: it.value,
        }))
      : [];
    updateLocalFilter('serviceGroups', selectedServiceGroups);
    updateLocalFilter(
      'clinics',
      sidebarFilters.clinics.filter(clinic =>
        (value || []).some(serviceGroup => serviceGroup.value === clinic.serviceGroupId),
      ),
    );
  };

  const handleTherapyTypeChange = value => {
    const selectedTherapyTypes = value
      ? value.map(it => ({
          displayValue: it.label,
          idValue: it.value,
        }))
      : [];
    updateLocalFilter('therapyTypes', selectedTherapyTypes);
  };

  const handleTaskClinicChange = value => {
    const selectedTaskClinics = value
      ? value.map(clinic => ({
          displayValue: clinic.label,
          idValue: clinic.value,
          serviceGroupId: clinic.service_group_id,
        }))
      : [];
    updateLocalFilter('clinics', selectedTaskClinics);
  };

  const handleTimeChange = value => {
    updateLocalFilter('date', value);
    updateLocalFilter('from', null);
    updateLocalFilter('to', null);
    if (value === 'ALL') {
      updateLocalFilter('excludeOverdueARs', false);
    }
  };

  const handleLastNamesByLetterChange = value => {
    updateLocalFilter('lastNamesByLetter', value);
  };

  const handleAuditChange = value => {
    let retArrRule = [];
    let retArrCategory = [];
    if (value && value.rule && value.rule.length > 0) {
      retArrRule = retArrRule.concat(value.rule.map(v => v.value));
    } else if (
      !value.rule &&
      value.rule !== null &&
      sidebarFilters.auditWarningsRule &&
      sidebarFilters.auditWarningsRule.length > 0
    ) {
      retArrRule = sidebarFilters.auditWarningsRule;
    }
    if (value && value.category && Array.isArray(value.category) && value.category.length > 0) {
      retArrCategory = value.category.map(v => v.value);
    } else if (
      !value.category &&
      value.category !== null &&
      sidebarFilters.auditWarningsCategory.length > 0
    ) {
      retArrCategory = sidebarFilters.auditWarningsCategory;
    }
    retArrRule = retArrRule.flat(Infinity).map(val => Number(val));
    updateLocalFilter(
      'auditWarningsCategory',
      retArrCategory.sort((a, b) => a - b),
    );
    updateLocalFilter(
      'auditWarningsRule',
      retArrRule.sort((a, b) => a - b),
    );
  };

  const handleStatusChange = statusSelectedOptions => {
    const reasonStatus = statusSelectedOptions?.filter(opt => opt.value > 0);
    const taskStatusWithLabel = statusSelectedOptions?.filter(opt => !(opt.value > 0));

    const statusIds = (taskStatusWithLabel || []).reduce(
      (accum, statusSelectedOption) => [...accum, ...statusIdsLookup[statusSelectedOption.value]],
      [],
    );
    setTaskStatusValues(taskStatusWithLabel || []);

    updateLocalFilter('statusIds', {
      ...(sidebarFilters.statusIds || {}),
      [sidebarTaskType]: statusIds.length ? statusIds : undefined,
    });

    updateLocalFilter(
      'fcInProgressReason',
      (reasonStatus || []).map(reason => reason.value),
    );
  };

  const handleSelectAllSecureLinkFCFilters = useCallback(
    ({ target: { checked } }) => {
      setTaskStatusValues(() =>
        checked
          ? Object.keys(statusIdsLookup).reduce((acc, key) => {
              if (
                statusIdsLookup[key].some(id => FILL_COORDINATION_STATUS_SECURE_LINK.includes(id))
              ) {
                acc.push({ value: key, label: key });
              }
              return acc;
            }, [])
          : [],
      );

      updateLocalFilter('statusIds', {
        ...(sidebarFilters.statusIds || {}),
        [sidebarTaskType]: checked ? FILL_COORDINATION_STATUS_SECURE_LINK : undefined,
      });
    },
    [setSidebarFilter, sidebarFilters.statusIds, sidebarTaskType, statusIdsLookup],
  );

  const handleReasonInProgressChange = reasonSelectedOptions => {
    updateLocalFilter(
      'fcInProgressReason',
      (reasonSelectedOptions || []).map(reason => reason.value),
    );
  };

  const handleDispensingStatusChange = selectedEnrollmentStatuses => {
    setEnrollmentStatusValues(selectedEnrollmentStatuses);

    updateLocalFilter(
      'dispensingEnrollmentStatusIds',
      (selectedEnrollmentStatuses || []).map(enrollmentStatus => enrollmentStatus.value),
    );
  };

  const handleAdministrationStatusChange = selectedAdministrationStatuses => {
    setAdministrationStatusValues(selectedAdministrationStatuses);
    updateLocalFilter(
      'administrationStatusIds',
      (selectedAdministrationStatuses || []).map(adminStatus => adminStatus.value),
    );
  };

  const handleARReasonChange = selectedReason => {
    setARReasonValues(selectedReason);
  };

  useEffect(() => {
    updateLocalFilter(
      'arReasonIds',
      (arReasonValues || []).map(reasons => reasons.value),
    );
  }, [arReasonValues]);

  // Handles selection of delivery methods and maps them to appropriate filter values
  const handleMethodOfDelivery = value => {
    const methodOfDeliveryArr = value && value.length > 0 ? value : [];

    updateLocalFilter(
      'fdcMethodsOfDelivery',
      methodOfDeliveryArr.map(type => type.value).sort((a, b) => a - b),
    );
  };

  const handleDisplayOnlyOptionChanged = value => {
    const methodOfDeliveryArr = value && value.length > 0 ? value : [];

    updateLocalFilter(
      'displayOnlyFdc',
      methodOfDeliveryArr.map(type => type.value).sort((a, b) => a - b),
    );
  };

  const handleAssistanceProgramChange = value => {
    updateLocalFilter(
      'assistanceProgram',
      value?.flatMap(v => v.value),
    );
  };

  const handleInternalFoundationProgramChange = value => {
    updateLocalFilter(
      'internalFoundation',
      value?.flatMap(v => v.value),
    );
  };

  const assistanceProgramDefaulValues = sidebarFilters.assistanceProgram?.flatMap(ap => {
    const current = getFinancialTypeFilterValueByKey(ap);
    return {
      label: current.label,
      value: current.key,
    };
  });

  const internalFoundationDefaultValues = sidebarFilters.internalFoundation?.flatMap(ap => {
    const [current] = internalFoundationFilterValues.filter(x => x.value === ap);
    return {
      label: current.label,
      value: current.value,
    };
  });

  const handleTaskTagChange = value => {
    switch (sidebarTaskType) {
      case PA:
        updateLocalFilter('paNewRenewal', value);
        break;
      case FA:
        updateLocalFilter('faNewRenewal', value);
        break;
      case CSL:
        updateLocalFilter('cslInitialOngoing', value);
        break;
      case OR: {
        let retArrType = [];
        let retArrCategory = [];
        if (value && value.type && value.type.length > 0) {
          retArrType = retArrType.concat(value.type.map(v => v.value.split(',')));
        } else if (
          !value.type &&
          value.type !== null &&
          sidebarFilters.orType &&
          sidebarFilters.orType.length > 0
        ) {
          retArrType = sidebarFilters.orType;
        }
        if (value && value.category && Array.isArray(value.category) && value.category.length > 0) {
          retArrCategory = value.category.map(v => v.value);
        } else if (
          !value.category &&
          value.category !== null &&
          sidebarFilters.orCategory.length > 0
        ) {
          retArrCategory = sidebarFilters.orCategory;
        }
        retArrType = retArrType.flat(Infinity).map(val => Number(val));
        updateLocalFilter(
          'orCategory',
          retArrCategory.sort((a, b) => a - b),
        );
        updateLocalFilter(
          'orType',
          retArrType.sort((a, b) => a - b),
        );
        break;
      }
      case QRE: {
        let qreRetArrType = [];
        let qreRetArrCategory = [];
        let qreRetArrResponsibleParty = [];
        if (value && value.type && value.type.length > 0) {
          qreRetArrType = qreRetArrType.concat(value.type.map(v => v.value));
        } else if (
          !value.type &&
          value.type !== null &&
          sidebarFilters.incidentType &&
          sidebarFilters.incidentType.length > 0
        ) {
          qreRetArrType = sidebarFilters.incidentType;
        }
        if (value && value.category && Array.isArray(value.category) && value.category.length > 0) {
          qreRetArrCategory = value.category.map(v => v.value);
        } else if (
          !value.category &&
          value.category !== null &&
          sidebarFilters.incidentCategory.length > 0
        ) {
          qreRetArrCategory = sidebarFilters.incidentCategory;
        }
        if (
          value &&
          value.responsibleParty &&
          Array.isArray(value.responsibleParty) &&
          value.responsibleParty.length > 0
        ) {
          qreRetArrResponsibleParty = value.responsibleParty.map(v => v.value);
        } else if (
          !value.responsibleParty &&
          value.responsibleParty !== null &&
          sidebarFilters.incidentResponsibleParty.length > 0
        ) {
          qreRetArrResponsibleParty = sidebarFilters.incidentResponsibleParty;
        }
        qreRetArrType = qreRetArrType.flat(Infinity).map(val => Number(val));
        updateLocalFilter(
          'incidentCategory',
          qreRetArrCategory.sort((a, b) => a - b),
        );
        updateLocalFilter(
          'incidentType',
          qreRetArrType.sort((a, b) => a - b),
        );
        updateLocalFilter(
          'incidentResponsibleParty',
          qreRetArrResponsibleParty.sort((a, b) => a - b),
        );
        break;
      }
      case AR: {
        // / eslint-disable-next-line no-case-declarations
        let arTypeArr = [];
        let arCommunicationTypeArr = [];
        if (value && value.arType && Array.isArray(value.arType) && value.arType.length > 0) {
          arTypeArr = value.arType.map(v => v.value);
        } else if (!value.arType && value.arType !== null && sidebarFilters.arType.length > 0) {
          arTypeArr = sidebarFilters.arType;
        }
        if (
          value &&
          value.arCommunicationType &&
          Array.isArray(value.arCommunicationType) &&
          value.arCommunicationType.length > 0
        ) {
          arCommunicationTypeArr = value.arCommunicationType.map(v => v.value);
        } else if (
          !value.arCommunicationType &&
          value.arCommunicationType !== null &&
          sidebarFilters &&
          sidebarFilters.arCommunicationType &&
          sidebarFilters.arCommunicationType.length > 0
        ) {
          arCommunicationTypeArr = sidebarFilters.arCommunicationType;
        }
        updateLocalFilter(
          'arType',
          arTypeArr.sort((a, b) => a - b),
        );
        updateLocalFilter(
          'arCommunicationType',
          arCommunicationTypeArr.sort((a, b) => a - b),
        );

        break;
      }
      default:
        break;
    }
  };

  const handleFillCycleChange = value => {
    updateLocalFilter('fillCycle', value);
  };

  const handleDispensingPharmacyChange = values => {
    updateLocalFilter(
      'dispensingPharmacies',
      (values || []).map(({ value: _v, label: _l }) => ({
        id: _v,
        idValue: _v,
        display_name: _l,
      })),
    );
  };

  // #endregion component functions

  // #region  side effects

  useEffect(() => {
    if (
      sidebarTaskType &&
      taskStatuses.statuses &&
      sidebarTaskType !== THERAPIES &&
      sidebarTaskType !== SMS_RELATED_TASK
    ) {
      const { statusIdsOptionLookup, statusSelectorOptions } = buildTaskStatusLookupOptions();
      setStatusIdsLookup(statusIdsOptionLookup);
      setStatusOptions(statusSelectorOptions);
      setTaskStatusValues(buildTaskStatusValues());
    }

    if (sidebarTaskType === THERAPIES) {
      setEnrollmentStatusValues(
        buildInitialEnrollmentStatusValues(sidebarFilters?.dispensingEnrollmentStatusIds || []),
      );
      setAdministrationStatusValues(
        buildInitialAdministrationStatusValues(sidebarFilters?.administrationStatusIds || []),
      );
    }
  }, [sidebarTaskType]);

  useEffect(() => {
    const taskLabels = taskStatusValues.map(item => item.label);
    const expectedLabels = Object.keys(statusIdsLookup).filter(key =>
      statusIdsLookup[key].some(id => FILL_COORDINATION_STATUS_SECURE_LINK.includes(id)),
    );

    const allPresent = expectedLabels.every(label => taskLabels.includes(label));
    const onlyExpectedLabels = taskLabels.every(label => expectedLabels.includes(label));

    setIsSelectAllSecureLinkFC(allPresent && onlyExpectedLabels);
  }, [statusIdsLookup, taskStatusValues]);

  // #endregion side effects

  // #region renderers

  const taskTagFilters = () => {
    const labelArr = [];
    switch (sidebarTaskType) {
      case PA:
        return (
          <Grid item xs={12} className={classes.serviceGroupContainer}>
            <RadioGroup horizontal wrap onChange={value => handleTaskTagChange(value)}>
              <RadioButton value={0} checked={localFilters.paNewRenewal === 0}>
                New
              </RadioButton>
              <RadioButton
                value={1}
                checked={localFilters.paNewRenewal && localFilters.paNewRenewal === 1}
                disabled={!dateChipsEnabled}
              >
                Renewal
              </RadioButton>
              <RadioButton
                value={-1}
                checked={localFilters.paNewRenewal && localFilters.paNewRenewal === -1}
                disabled={!dateChipsEnabled}
              >
                All
              </RadioButton>
            </RadioGroup>
          </Grid>
        );
      case FA:
        return (
          <>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <RadioGroup horizontal wrap onChange={value => handleTaskTagChange(value)}>
                <RadioButton value={0} checked={localFilters.faNewRenewal === 0}>
                  New
                </RadioButton>
                <RadioButton
                  value={1}
                  checked={localFilters.faNewRenewal && localFilters.faNewRenewal === 1}
                  disabled={!dateChipsEnabled}
                >
                  Renewal
                </RadioButton>
                <RadioButton
                  value={-1}
                  checked={localFilters.faNewRenewal && localFilters.faNewRenewal === -1}
                  disabled={!dateChipsEnabled}
                >
                  All
                </RadioButton>
              </RadioGroup>
            </Grid>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={assistanceProgramDefaulValues}
                name="assistanceProgram"
                label="Assistance Programs"
                handleOnChange={value => handleAssistanceProgramChange(value)}
                fields={financialTypeFilterValues.map(x => ({
                  label: x.label,
                  value: x.key,
                }))}
              />
            </Grid>
          </>
        );
      case FC:
        return (
          <Grid item xs={12} className={classes.serviceGroupContainer}>
            <ReactSelect
              defaultValues={internalFoundationDefaultValues}
              name="assistanceProgram"
              label="Internal Foundation"
              handleOnChange={value => handleInternalFoundationProgramChange(value)}
              fields={internalFoundationFilterValues.map(x => ({
                label: x.label,
                value: x.value,
              }))}
            />
          </Grid>
        );
      case CSL:
        return (
          <Grid item xs={12} className={classes.serviceGroupContainer}>
            <RadioGroup horizontal wrap onChange={value => handleTaskTagChange(value)}>
              <RadioButton
                value={1}
                checked={localFilters.cslInitialOngoing && localFilters.cslInitialOngoing === 1}
              >
                Initial
              </RadioButton>
              <RadioButton
                value={2}
                checked={localFilters.cslInitialOngoing && localFilters.cslInitialOngoing === 2}
              >
                Ongoing
              </RadioButton>
              <RadioButton
                value={3}
                checked={localFilters.cslInitialOngoing && localFilters.cslInitialOngoing === 3}
              >
                Check-In
              </RadioButton>
              <RadioButton
                value={-1}
                checked={localFilters.cslInitialOngoing && localFilters.cslInitialOngoing === -1}
              >
                All
              </RadioButton>
            </RadioGroup>
          </Grid>
        );
      case OR:
        return (
          <>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  localFilters.orCategory && localFilters.orCategory.length > 0
                    ? localFilters.orCategory.map(category => ({
                        value: category,
                        label: taskStatuses.outreach_categories.find(val => val.id === category)
                          ? taskStatuses.outreach_categories.find(val => val.id === category).name
                          : '',
                      }))
                    : null
                }
                name="outreachCategory"
                label="Outreach Categories"
                handleOnChange={value => handleTaskTagChange({ category: value })}
                fields={taskStatuses.outreach_categories
                  .filter(cat => !taskStatuses.outreach_categories.includes(cat.id))
                  .map(cat => ({
                    value: cat.id,
                    label: cat.name,
                  }))}
              />
            </Grid>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={initialTypesForOR()}
                name="outreachType"
                label="Outreach Type"
                handleOnChange={value => handleTaskTagChange({ type: value })}
                fields={outreachTypeByName.map(type => ({
                  value: type.related,
                  label: type.name,
                }))}
              />
            </Grid>
          </>
        );
      case QRE:
        return (
          <>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  localFilters.incidentCategory && localFilters.incidentCategory.length > 0
                    ? localFilters.incidentCategory.map(category => ({
                        value: category,
                        label: lookups.incidentCategories.find(val => val.id === category)
                          ? lookups.incidentCategories.find(val => val.id === category).name
                          : '',
                      }))
                    : null
                }
                name="incidentCategory"
                label="Incident Categories"
                handleOnChange={value => handleTaskTagChange({ category: value })}
                fields={lookups.incidentCategories
                  .filter(cat => !lookups.incidentCategories.includes(cat.id))
                  .map(cat => ({
                    value: cat.id,
                    label: cat.name,
                  }))}
              />
            </Grid>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  localFilters.incidentType && localFilters.incidentType.length > 0
                    ? localFilters.incidentType.map(type => ({
                        value: type,
                        label: lookups.incidentTypes.find(val => val.id === type)
                          ? lookups.incidentTypes.find(val => val.id === type).name
                          : '',
                      }))
                    : null
                }
                name="incidentType"
                label="Incident Type"
                handleOnChange={value => handleTaskTagChange({ type: value })}
                fields={lookups.incidentTypes.map(type => ({
                  value: type.id,
                  label: type.name,
                }))}
              />
            </Grid>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  qreResponsibles &&
                  localFilters.incidentResponsibleParty &&
                  localFilters.incidentResponsibleParty.length > 0
                    ? localFilters.incidentResponsibleParty.map(f =>
                        qreResponsibles.find(v => v.value === f),
                      )
                    : null
                }
                isDisabled={onlyActiveUserQRE}
                name="responsibleParty"
                label="Responsible Party"
                handleOnChange={value => handleTaskTagChange({ responsibleParty: value })}
                fields={qreResponsibles}
              />
            </Grid>

            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <FormControlLabel
                control={<Checkbox />}
                checked={onlyActiveUserQRE}
                onChange={e => handleOnlyUserTasksQre(e)}
                label="Show only QRE's assigned to me"
              />
            </Grid>
          </>
        );
      case AR:
        return (
          <>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  localFilters.arType && localFilters.arType.length > 0
                    ? localFilters.arType.map(category => ({
                        value: category,
                        label: arListValues.some(val => val.value === category)
                          ? arListValues.find(val => val.value === category).label
                          : '',
                      }))
                    : null
                }
                name="arType"
                label="Creation type"
                handleOnChange={value => handleTaskTagChange({ arType: value })}
                fields={arListValues}
              />
            </Grid>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                defaultValues={
                  localFilters.arCommunicationType && localFilters.arCommunicationType.length > 0
                    ? localFilters.arCommunicationType.map(category => ({
                        value: category,
                        label: arCommunicationTypeValues.some(val => val.value === category)
                          ? arCommunicationTypeValues.find(val => val.value === category).label
                          : '',
                      }))
                    : null
                }
                name="arCommunicationType"
                label="Communication type"
                handleOnChange={value => handleTaskTagChange({ arCommunicationType: value })}
                fields={arCommunicationTypeValues}
              />
            </Grid>
          </>
        );

      case INT:
        return (
          <>
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <FormControlLabel
                control={<Checkbox />}
                checked={localFilters.medicationAvailabiltiyINT}
                onChange={e => handleMedicationAvailabiltiyINT(e)}
                label="Show only Medication Availability INTs"
              />
            </Grid>
          </>
        );
      default:
        break;
    }
    if (labelArr.length > 0) {
      return labelArr;
    }
    return null;
  };

  const disableExcludeOverdueARCheckbox =
    (localFilters.date === 'ALL' || localFilters.date === null) &&
    !localFilters.to &&
    !localFilters.from;

  const sideBarTaskFilters = (
    <Grid container direction="column" wrap="nowrap" className={classes.filterPanelContainer}>
      {sidebarTaskType !== STAR ? (
        <>
          <Grid item xs={12}>
            <SubHeader name="TIME" />
          </Grid>
          <Grid item xs={12}>
            <RadioGroup
              horizontal
              wrap
              onChange={value => handleTimeChange(value)}
              style={{ width: '100%' }}
            >
              <RadioButton
                value={DATE_OPTIONS.YESTERDAY}
                checked={dateChipsEnabled && radioCheckedDate[DATE_OPTIONS.YESTERDAY]}
                disabled={!dateChipsEnabled}
              >
                Yesterday
              </RadioButton>
              <RadioButton
                value={DATE_OPTIONS.TODAY}
                checked={dateChipsEnabled && radioCheckedDate[DATE_OPTIONS.TODAY]}
                disabled={!dateChipsEnabled}
              >
                Today
              </RadioButton>
              <RadioButton
                value={DATE_OPTIONS.TOMORROW}
                checked={dateChipsEnabled && radioCheckedDate[DATE_OPTIONS.TOMORROW]}
                disabled={!dateChipsEnabled}
              >
                Tomorrow
              </RadioButton>
              <RadioButton
                value={DATE_OPTIONS.NEXT_7_DAYS}
                checked={dateChipsEnabled && radioCheckedDate[DATE_OPTIONS.NEXT_7_DAYS]}
                disabled={!dateChipsEnabled}
              >
                Next 7 Days
              </RadioButton>
              <RadioButton
                value={DATE_OPTIONS.NEXT_14_DAYS}
                checked={dateChipsEnabled && radioCheckedDate[DATE_OPTIONS.NEXT_14_DAYS]}
                disabled={!dateChipsEnabled}
              >
                Next 14 Days
              </RadioButton>
            </RadioGroup>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={6}>
              <Typography variant="caption" component="div">
                From
              </Typography>
              <DatetimePicker
                value={localFilters.from}
                onChange={e => handleFromDateChange(e)}
                dateFormat="MM/dd/yyyy"
                placeholder="mm/dd/yyyy"
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" component="div">
                To
              </Typography>
              <DatetimePicker
                value={localFilters.to}
                onChange={e => handleToDateChange(e)}
                dateFormat="MM/dd/yyyy"
                placeholder="mm/dd/yyyy"
                style={{ width: '100%' }}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} style={{ marginBottom: 16 }}>
            <button
              type="button"
              onClick={handleApplyFilters}
              className={classes.applyFilterButton}
            >
              Apply Filters
            </button>
          </Grid>
          {sidebarTaskType === FC && (
            <>
              <Grid item xs={12} direction="row" className={classes.serviceGroupContainer}>
                <MediumSpacer />
                <SubHeader name="LAST NAMES BY LETTER" />
                <RadioGroup
                  horizontal
                  wrap
                  onChange={value => handleLastNamesByLetterChange(value)}
                >
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.NONE}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.NONE}
                  >
                    All
                  </RadioButton>
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.A_F}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.A_F}
                  >
                    A - F
                  </RadioButton>
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.G_K}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.G_K}
                  >
                    G - K
                  </RadioButton>
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.L_P}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.L_P}
                  >
                    L - P
                  </RadioButton>
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.Q_U}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.Q_U}
                  >
                    Q - U
                  </RadioButton>
                  <RadioButton
                    value={LAST_NAMES_BY_LETTER.V_Z}
                    checked={localFilters.lastNamesByLetter === LAST_NAMES_BY_LETTER.V_Z}
                  >
                    V - Z
                  </RadioButton>
                </RadioGroup>
              </Grid>
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <MediumSpacer />
                <ReactSelect
                  data-qa-id=""
                  label="Display only"
                  defaultValues={null}
                  isMulti
                  value={(localFilters.displayOnlyFC || []).map(it =>
                    (lookups.lookupDisplayOnlyFillCoordination || []).find(l => it === l.value),
                  )}
                  handleOnChange={handleDisplayOnlyFC}
                  fields={lookups.lookupDisplayOnlyFillCoordination}
                />
                <MediumSpacer />
                <FillCycleFilter
                  value={localFilters.fillCycle || 1}
                  onChange={handleFillCycleChange}
                />
              </Grid>
            </>
          )}
          {sidebarTaskType === DC && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <MediumSpacer />
              <ReactSelect
                data-qa-id=""
                label="Display only"
                defaultValues={null}
                isMulti
                value={(localFilters.displayOnlyDC || []).map(it =>
                  (lookups.lookupDisplayOnlyDataCollect || []).find(l => it === l.value),
                )}
                handleOnChange={handleDisplayOnlyDC}
                fields={lookups.lookupDisplayOnlyDataCollect}
              />
            </Grid>
          )}
          {sidebarTaskType === CSL && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <MediumSpacer />
              <ReactSelect
                data-qa-id=""
                label="Display only"
                defaultValues={null}
                isMulti
                value={(localFilters.displayOnlyCSL || []).map(it =>
                  (lookups.lookupDisplayOnlyCounseling || []).find(l => it === l.value),
                )}
                handleOnChange={handleDisplayOnlyCSL}
                fields={lookups.lookupDisplayOnlyCounseling}
              />
            </Grid>
          )}
          {sidebarTaskType === INT && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <MediumSpacer />
              <ReactSelect
                data-qa-id=""
                label="Display only"
                defaultValues={null}
                isMulti
                value={(localFilters.displayOnlyINT || []).map(it =>
                  (lookups.lookupDisplayOnlyIntervention || []).find(l => it === l.value),
                )}
                handleOnChange={handleDisplayOnlyINT}
                fields={lookups.lookupDisplayOnlyIntervention}
              />
            </Grid>
          )}
          {sidebarTaskType === RS && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <MediumSpacer />
              <ReactSelect
                data-qa-id=""
                label="Display only"
                defaultValues={null}
                isMulti
                value={(localFilters.displayOnlyRS || []).map(it =>
                  (lookups.lookupDisplayOnlyRiskStrat || []).find(l => it === l.value),
                )}
                handleOnChange={handleDisplayOnlyRS}
                fields={lookups.lookupDisplayOnlyRiskStrat}
              />
            </Grid>
          )}
          {sidebarTaskType === 'AR' && (
            <Grid sx={{ mt: 2.5 }}>
              <FormControlLabel
                control={<Checkbox disabled={disableExcludeOverdueARCheckbox} />}
                checked={localFilters.excludeOverdueARs}
                onChange={e => handleExcludeOverdueARsChange(e)}
                label="Exclude Overdue ARs"
              />
            </Grid>
          )}
          <SmallSpacer />
          <Grid item xs={12} className={classes.serviceGroupContainer}>
            <SubHeader name="SERVICE GROUP" />
            <SmallSpacer />
            <ReactSelect
              defaultValues={localFilters.serviceGroups.map(serviceGroup => ({
                value: serviceGroup.idValue,
                label: `${serviceGroup.displayValue}`,
              }))}
              name="serviceGroups"
              label="Service Groups"
              handleOnChange={e => handleServiceGroupChange(e)}
              fields={lookups.serviceGroups
                .filter(
                  serviceGroup =>
                    !localFilters.serviceGroups.some(x => x.idValue === serviceGroup.id),
                )
                .map(serviceGroup => ({
                  value: serviceGroup.id,
                  label: serviceGroup.display_name,
                }))}
            />
            <SmallSpacer />
            {sidebarTaskType !== SMS_RELATED_TASK &&
              sidebarTaskType !== DUR &&
              sidebarTaskType !== RS &&
              localFilters.serviceGroups.length > 0 && (
                <ReactSelect
                  defaultValues={localFilters.clinics.map(clinic => ({
                    value: `${clinic.idValue}`,
                    label: `${clinic.displayValue}`,
                  }))}
                  name="taskClinics"
                  label="Task Clinics"
                  handleOnChange={e => handleTaskClinicChange(e)}
                  fields={clinicsForServiceGroups
                    .filter(clinic => !localFilters.clinics.some(x => x.idValue === clinic.id))
                    .map(clinic => ({
                      value: clinic.id,
                      label: clinic.name,
                      service_group_id: clinic.service_group_id,
                    }))}
                />
              )}
            <MediumSpacer />
          </Grid>
          {/* {sidebarTaskType === FA && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <FormControlLabel
                control={<Checkbox />}
                checked={localFilters.hideInternalFundingFA}
                onChange={e => handleHideInternalFundingFA(e)}
                label="Hide FAs with Internal Funding or Free Drugs"
              />
            </Grid>
          )} */}
          {sidebarTaskType !== SMS_RELATED_TASK &&
            sidebarTaskType !== AR &&
            sidebarTaskType !== DUR &&
            sidebarTaskType !== RS &&
            sidebarTaskType !== QRE && (
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <SubHeader name="Therapies" />
                <SmallSpacer />
                <ReactSelect
                  defaultValues={localFilters.therapyTypes.map(therapyType => ({
                    value: therapyType.idValue,
                    label: `${therapyType.displayValue}`,
                  }))}
                  name="therapyTypes"
                  label="Therapy Types"
                  handleOnChange={e => handleTherapyTypeChange(e)}
                  fields={lookups.therapyTypes.map(therapyType => ({
                    value: therapyType.id,
                    label: therapyType.type,
                  }))}
                />
              </Grid>
            )}
          <Grid item xs={12}>
            {taskTagFilters({ style: { width: '100%' } })}
          </Grid>
          {queueAllowsAuditFilters.indexOf(sidebarTaskType) > -1 && (
            <>
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <ReactSelect
                  defaultValues={
                    localFilters.auditWarningsCategory &&
                    localFilters.auditWarningsCategory.length > 0
                      ? localFilters.auditWarningsCategory.map(category => ({
                          value: category,
                          label: getAuditCategory(category),
                        }))
                      : null
                  }
                  name="auditCategory"
                  label="Audit Categories"
                  handleOnChange={value => handleAuditChange({ category: value })}
                  fields={auditCategoriesRaw(lookups).map(cat => ({
                    value: cat,
                    label: getAuditCategory(cat),
                  }))}
                />
              </Grid>
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <ReactSelect
                  defaultValues={initialTypesForAuditRule()}
                  name="auditRule"
                  label="Audit Rule"
                  handleOnChange={value => handleAuditChange({ rule: value })}
                  fields={
                    lookups.auditConstants
                      ? lookups.auditConstants.map(cat => ({
                          value: cat.arbor_audit_definition_id,
                          label: cat.audit_item,
                        }))
                      : []
                  }
                />
              </Grid>
            </>
          )}
          {sidebarTaskType === FC && (
            <>
              <SmallSpacer />
              <FormControlLabel
                control={<Checkbox />}
                checked={isSelectAllSecureLinkFC}
                onChange={e => handleSelectAllSecureLinkFCFilters(e)}
                label="Select All Secure Link FC Filters"
              />
              <SmallSpacer />
            </>
          )}
          {sidebarTaskType !== THERAPIES && sidebarTaskType !== SMS_RELATED_TASK && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                value={[...taskStatusValues]}
                name="taskStatus"
                label="Task Status"
                handleOnChange={handleStatusChange}
                fields={[...statusOptions]}
              />
              {sidebarTaskType === FC &&
                taskStatusValues?.findIndex(taskStatus => taskStatus.value === 'In Progress') >=
                  0 && (
                  <>
                    <SmallSpacer />
                    <Grid item xs={12} className={classes.serviceGroupContainer}>
                      <ReactSelect
                        value={fcInProgressReasons.filter(
                          lkp =>
                            localFilters.fcInProgressReason.findIndex(
                              value => lkp.value === value,
                            ) >= 0,
                        )}
                        name="inProgressReason"
                        label="In Progress Reason"
                        handleOnChange={handleReasonInProgressChange}
                        fields={fcInProgressReasons}
                      />
                    </Grid>
                  </>
                )}
            </Grid>
          )}
          {(sidebarTaskType === FC || sidebarTaskType === FDC) && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <DispensingPharmacyFilter
                value={
                  Array.isArray(localFilters.dispensingPharmacies)
                    ? localFilters.dispensingPharmacies.map(pharmacy => ({
                        value: pharmacy.id ?? pharmacy.value ?? '',
                        label:
                          pharmacy.display_name ||
                          pharmacy.name ||
                          pharmacy.label ||
                          String(pharmacy.id ?? pharmacy.value ?? 'No Pharmacy'),
                      }))
                    : []
                }
                onChange={handleDispensingPharmacyChange}
                fields={
                  Array.isArray(lookups.lookupPharmacies) && lookups.lookupPharmacies.length > 0
                    ? lookups.lookupPharmacies.map(pharmacy => ({
                        value: pharmacy.id ?? pharmacy.value ?? '',
                        label:
                          pharmacy.display_name ||
                          pharmacy.name ||
                          pharmacy.label ||
                          String(pharmacy.id ?? pharmacy.value ?? 'No Pharmacy'),
                      }))
                    : [{ value: '', label: 'No Pharmacy' }]
                }
              />
              <MediumSpacer />
            </Grid>
          )}
          {sidebarTaskType === THERAPIES && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                value={enrollmentStatusValues}
                name="dispensingStatus"
                label="Dispensing Status"
                handleOnChange={handleDispensingStatusChange}
                fields={buildEnrollmentStatusOptions()}
              />
            </Grid>
          )}
          {sidebarTaskType === THERAPIES && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                value={administrationStatusValues}
                name="administrationStatus"
                label="Administration Status"
                handleOnChange={handleAdministrationStatusChange}
                fields={buildAdministrationStatusOptions()}
              />
            </Grid>
          )}
          {sidebarTaskType === FDC && (
            <>
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <SubHeader name="DISPLAY ONLY" />
                <SmallSpacer />
                <ReactSelect
                  defaultValues={
                    localFilters.displayOnlyFdc && localFilters.displayOnlyFdc.length > 0
                      ? localFilters.displayOnlyFdc.map(type => ({
                          value: type,
                          label:
                            displayOnlyFdcValues
                              .filter(val => !val.disabled)
                              .find(val => val.value === type)?.label || '',
                        }))
                      : null
                  }
                  name="fdcDisplayOnly"
                  label="Display Only Options"
                  handleOnChange={handleDisplayOnlyOptionChanged}
                  fields={displayOnlyFdcValues.filter(val => !val.disabled)}
                />
              </Grid>
              <Grid item xs={12} className={classes.serviceGroupContainer}>
                <SubHeader name="METHOD OF DELIVERY" />
                <SmallSpacer />
                <ReactSelect
                  defaultValues={
                    localFilters.fdcMethodsOfDelivery &&
                    localFilters.fdcMethodsOfDelivery.length > 0
                      ? localFilters.fdcMethodsOfDelivery.map(type => ({
                          value: type,
                          label:
                            methodOfDeliveryForCustomer
                              .filter(val => !val.disabled)
                              .find(val => val.value === type)?.label || '',
                        }))
                      : null
                  }
                  name="fdcMethodsOfDelivery"
                  label="Method of Delivery"
                  handleOnChange={handleMethodOfDelivery}
                  fields={methodOfDeliveryForCustomer.filter(val => !val.disabled)}
                  forceScrollbar
                />
              </Grid>
            </>
          )}
          {sidebarTaskType === 'AR' && (
            <Grid item xs={12} className={classes.serviceGroupContainer}>
              <ReactSelect
                value={arReasonValues}
                name="arReasonValues"
                label="Reason"
                handleOnChange={handleARReasonChange}
                fields={buildOutreachReasons()}
              />
            </Grid>
          )}
        </>
      ) : (
        <Grid item xs={12} className={classes.noFiltersContainer}>
          Filters are not available for the Starred Patients queue
        </Grid>
      )}
      <Grid item xs={12} className={classes.taskPanePaddingBottom} />
    </Grid>
  );

  return sideBarTaskFilters;

  // #endregion renders
};

function mapStateToProps(state) {
  const { lookups, sidebarFilters, sidebarTaskType, taskStatuses, selectedPatientId } = state;
  return {
    lookups,
    sidebarFilters: sidebarFilters.data.task,
    selectedPatientId,
    sidebarTaskType,
    taskStatuses,
  };
}

export default compose(
  withStyles(styles, { withTheme: true }),
  connect(mapStateToProps, {
    setSidebarFilter,
  }),
)(TaskFilterPanel);
